import {
  BulkActionContainerCol,
  Col,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  FusionMelcoModelsColorCollection,
  Search,
  useTableSelection,
  TableCellLink,
} from "melco-shared-logic";
import { useColorCollections } from "../../../hooks/libraries/colorCollections/useColorCollections";
import { BulkActionDeleteColorCollections } from "./BulkActionDeleteColorCollections";
import { PublishedState } from "../../state/PublishedState";

const DETAIL_VIEW_PATH = "/libraries/color-collections/embroidery";

export const ColorCollectionsList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useColorCollections();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: FusionMelcoModelsColorCollection) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    {
      title: t("color_collections_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsColorCollection) => (
        <ValueOrFallback
          value={item.name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("color_collections_list.columns.type"),
      key: "service_name",
      dataIndex: "service_name",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsColorCollection) => (
        <ValueOrFallback
          value={
            item.service_name &&
            t(
              `libraries.color_collections.service_types.${item.service_name.toLowerCase()}` as any
            )
          }
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("color_collections_list.columns.colors"),
      key: "color_count",
      dataIndex: "color_count",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsColorCollection) => (
        <ValueOrFallback
          value={item.color_count}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("color_collections_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsColorCollection) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("color_collections_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsColorCollection) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
    {
      title: t("color_collections_list.columns.publish_state"),
      key: "publish_state",
      dataIndex: "publish_state",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsColorCollection) => {
        return <PublishedState publishedState={item.publish_state ?? 1} />;
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteColorCollections
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
            data={data}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("color_collections_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
